<template>
  <div class="user-management">
    <el-card shadow="never">
      <div class="filter-card">
        <div class="filter-bar">
          <div class="filter-group">
            <el-input
              v-model="searchPhone"
              placeholder="手机号"
              prefix-icon="el-icon-phone"
              clearable
              class="filter-input"
              @keyup.enter.native="handleSearch"
              @clear="handleSearch"
            />
            <el-select
              v-model="searchRole"
              placeholder="角色"
              clearable
              class="filter-select-sm"
              @change="handleSearch"
              @clear="handleSearch"
            >
              <el-option label="普通用户" :value="0" />
              <el-option label="创作者" :value="1" />
            </el-select>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">搜索</el-button>
            <el-button icon="el-icon-refresh" @click="refreshList">刷新</el-button>
            <el-button
              v-if="canEdit"
              type="success"
              icon="el-icon-plus"
              @click="openCreateDialog"
            >新增用户</el-button>
          </div>
        </div>
      </div>

      <div v-if="error" class="error">{{ error }}</div>
      <el-empty
        v-else-if="!loading && users.length === 0"
        :description="emptyDescription"
      />

      <div v-else>
        <el-table
          :data="users"
          border
          stripe
          v-loading="loading"
          element-loading-text="正在加载..."
          row-key="id"
        >
          <el-table-column prop="id" label="ID" width="80" align="center" />
          <el-table-column label="手机号" min-width="130" align="center" show-overflow-tooltip>
            <template slot-scope="scope">
              {{ scope.row.phone || '-' }}
            </template>
          </el-table-column>
          <el-table-column prop="status" label="状态" width="80" align="center">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.status === 0" type="danger" size="small" effect="plain">禁用</el-tag>
              <el-tag v-else-if="scope.row.status === 1" type="success" size="small" effect="plain">正常</el-tag>
              <span v-else class="text-muted">-</span>
            </template>
          </el-table-column>
          <el-table-column prop="role" label="角色" width="90" align="center">
            <template slot-scope="scope">
              {{ getRoleText(scope.row.role) }}
            </template>
          </el-table-column>
          <el-table-column prop="commissionRate" label="创作者比例" width="100" align="center">
            <template slot-scope="scope">
              {{ scope.row.commissionRate != null ? scope.row.commissionRate + '%' : '-' }}
            </template>
          </el-table-column>
          <el-table-column prop="deductRate" label="扣量比例" width="90" align="center">
            <template slot-scope="scope">
              {{ scope.row.deductRate != null ? scope.row.deductRate + '%' : '-' }}
            </template>
          </el-table-column>
          <el-table-column prop="adStatus" label="广告状态" width="90" align="center">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.adStatus === 0" type="danger" size="small">禁用</el-tag>
              <el-tag v-else-if="scope.row.adStatus === 1" type="success" size="small">正常</el-tag>
              <span v-else class="text-muted">-</span>
            </template>
          </el-table-column>
          <el-table-column prop="createdAt" label="注册时间" width="160" align="center" />
          <el-table-column label="操作" width="140" align="center" fixed="right">
            <template slot-scope="scope">
              <el-button
                size="mini"
                type="info"
                plain
                @click="openDetailDialog(scope.row)"
              >
                详情
              </el-button>
              <el-button
                v-if="canEdit"
                size="mini"
                type="primary"
                plain
                @click="openEditDialog(scope.row)"
              >
                编辑
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination-container">
          <el-pagination
            background
            layout="total, sizes, prev, pager, next, jumper"
            :current-page="pagination.page"
            :page-size="pagination.pageSize"
            :page-sizes="[10, 20, 50, 100]"
            :total="pagination.total"
            @current-change="handleCurrentChange"
            @size-change="handleSizeChange"
          />
        </div>
      </div>
    </el-card>

    <!-- 新增用户弹窗 -->
    <el-dialog
      title="新增用户"
      :visible.sync="createDialogVisible"
      width="520px"
      :close-on-click-modal="false"
      center
      append-to-body
    >
      <el-form ref="createFormRef" :model="createForm" :rules="createRules" label-width="110px">
        <el-form-item label="手机号" prop="phone">
          <el-input v-model="createForm.phone" maxlength="11" placeholder="用于登录的手机号" />
        </el-form-item>
        <el-form-item label="登录密码" prop="password">
          <el-input
            v-model="createForm.password"
            type="password"
            show-password
            maxlength="32"
            placeholder="请输入登录密码（6-32位）"
          />
        </el-form-item>
        <el-form-item label="角色" prop="role">
          <el-radio-group v-model="createForm.role">
            <el-radio :label="0">普通用户</el-radio>
            <el-radio :label="1">创作者</el-radio>
          </el-radio-group>
        </el-form-item>
        <template v-if="createForm.role === 1">
          <el-form-item label="创作者比例">
            <el-input-number v-model="createForm.commissionRate" :min="0" :precision="0" />
            <span style="margin-left:8px">%</span>
          </el-form-item>
          <el-form-item label="扣量比例">
            <el-input-number v-model="createForm.deductRate" :min="0" :max="100" :precision="0" />
            <span style="margin-left:8px">%</span>
          </el-form-item>
        </template>
        <el-form-item label="用户状态">
          <el-radio-group v-model="createForm.status">
            <el-radio :label="1">正常</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="广告状态">
          <el-radio-group v-model="createForm.adStatus">
            <el-radio :label="1">正常</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="createDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="createLoading" @click="handleCreateSubmit">确定</el-button>
      </div>
    </el-dialog>

    <!-- 编辑用户弹窗 -->
    <el-dialog
      title="编辑用户"
      :visible.sync="editDialogVisible"
      width="520px"
      :close-on-click-modal="false"
      center
      append-to-body
    >
      <el-form ref="editFormRef" :model="editForm" label-width="110px">
        <el-form-item label="角色">
          <el-radio-group v-model="editForm.role">
            <el-radio :label="0">普通用户</el-radio>
            <el-radio :label="1">创作者</el-radio>
          </el-radio-group>
        </el-form-item>
        <template v-if="editForm.role === 1">
          <el-form-item label="创作者比例">
            <el-input-number v-model="editForm.commissionRate" :min="0" :precision="0" />
            <span style="margin-left:8px">%</span>
          </el-form-item>
          <el-form-item label="扣量比例">
            <el-input-number v-model="editForm.deductRate" :min="0" :max="100" :precision="0" />
            <span style="margin-left:8px">%</span>
          </el-form-item>
          <el-form-item label="广告状态">
            <el-radio-group v-model="editForm.adStatus">
              <el-radio :label="0">禁用</el-radio>
              <el-radio :label="1">正常</el-radio>
            </el-radio-group>
          </el-form-item>
        </template>
        <el-form-item label="用户状态">
          <el-radio-group v-model="editForm.status">
            <el-radio :label="0">禁用</el-radio>
            <el-radio :label="1">正常</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="editDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleEditSubmit">保存</el-button>
      </div>
    </el-dialog>

    <!-- 用户详情弹窗 -->
    <el-dialog
      :title="'用户详情 - ' + (detailUser.phone || detailUser.id || '')"
      :visible.sync="detailDialogVisible"
      width="500px"
      :close-on-click-modal="false"
      center
      append-to-body
    >
      <div v-loading="detailLoading" class="detail-content">
        <div class="detail-grid">
          <div class="detail-item">
            <div class="detail-label">用户ID</div>
            <div class="detail-value">{{ detailUser.id || '-' }}</div>
          </div>
          <div class="detail-item">
            <div class="detail-label">手机号</div>
            <div class="detail-value">{{ detailUser.phone || '-' }}</div>
          </div>
          <div class="detail-item">
            <div class="detail-label">当前收入</div>
            <div class="detail-value highlight-green">{{ formatAmount(detailData.currentCoins) }}</div>
          </div>
          <div class="detail-item">
            <div class="detail-label">累计收入</div>
            <div class="detail-value highlight-gold">{{ formatAmount(detailData.totalEarned) }}</div>
          </div>
          <div class="detail-item">
            <div class="detail-label">待入款</div>
            <div class="detail-value highlight-blue">{{ formatAmount(detailData.deductAmount) }}</div>
          </div>
          <div class="detail-item">
            <div class="detail-label">今日收益</div>
            <div class="detail-value highlight-green">{{ formatAmount(detailData.todayRevenue) }}</div>
          </div>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="detailDialogVisible = false">关闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { get, put, post } from '@/utils/http'

export default {
  name: 'UserManagement',
  data() {
    return {
      users: [],
      loading: false,
      error: '',
      searchPhone: '',
      searchRole: '',
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      createDialogVisible: false,
      createLoading: false,
      createForm: {
        phone: '',
        password: '',
        role: 0,
        leaderId: '',
        commissionRate: 20,
        deductRate: 10,
        status: 1,
        adStatus: 1
      },
      createRules: {
        phone: [
          { required: true, message: '请输入手机号', trigger: 'blur' },
          { pattern: /^1\d{10}$/, message: '手机号格式不正确', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入登录密码', trigger: 'blur' },
          { min: 6, max: 32, message: '密码长度为6-32位', trigger: 'blur' }
        ],
        role: [{ required: true, message: '请选择角色', trigger: 'change' }]
      },
      editDialogVisible: false,
      editUser: null,
      detailDialogVisible: false,
      detailLoading: false,
      detailUser: {},
      detailData: {
        currentCoins: null,
        totalEarned: null,
        deductAmount: null,
        todayRevenue: null
      },
      editForm: {
        role: 0,
        commissionRate: null,
        deductRate: null,
        status: 0,
        adStatus: 0
      }
    }
  },
  computed: {
    canEdit() {
      return parseInt(localStorage.getItem('role')) === 0
    },
    emptyDescription() {
      if (this.searchPhone || (this.searchRole !== '' && this.searchRole != null)) {
        return '没有符合条件的用户'
      }
      return '暂无用户'
    }
  },
  mounted() {
    this.fetchUsers()
  },
  methods: {
    formatAmount(val) {
      if (val == null || val === '') return '-'
      return (val / 1000).toFixed(2)
    },
    getRoleText(role) {
      if (role == null || role === '') return '-'
      const map = { 0: '普通用户', 1: '创作者' }
      return map[role] || '-'
    },
    buildQueryParams(includePage = true) {
      const params = {}
      if (includePage) {
        params.page = this.pagination.page
        params.pageSize = this.pagination.pageSize
      }
      if (this.searchPhone) params.phone = this.searchPhone
      if (this.searchRole !== '' && this.searchRole !== null) params.role = this.searchRole
      return params
    },

    async fetchUsers() {
      this.loading = true
      this.error = ''
      try {
        const result = await get('/admin/users', this.buildQueryParams(true))
        const data = (result && result.data) ? result.data : {}
        this.users = Array.isArray(data.users) ? data.users : []
        this.pagination.total = data.total || 0
        this.pagination.pageSize = data.pageSize || this.pagination.pageSize
        this.pagination.page = data.page || this.pagination.page
      } catch (e) {
        this.error = e && e.message ? e.message : '获取用户列表失败'
        this.users = []
        this.pagination.total = 0
      } finally {
        this.loading = false
      }
    },

    handleSearch() {
      this.pagination.page = 1
      this.fetchUsers()
    },

    handleCurrentChange(page) {
      this.pagination.page = page
      this.fetchUsers()
    },

    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.page = 1
      this.fetchUsers()
    },

    refreshList() {
      this.searchPhone = ''
      this.searchRole = ''
      this.pagination.page = 1
      this.fetchUsers()
    },

    openCreateDialog() {
      this.createForm = {
        phone: '',
        password: '',
        role: 0,
        leaderId: '',
        commissionRate: 20,
        deductRate: 10,
        status: 1,
        adStatus: 1
      }
      this.createDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.createFormRef) this.$refs.createFormRef.clearValidate()
      })
    },

    handleCreateSubmit() {
      this.$refs.createFormRef.validate(async (valid) => {
        if (!valid) return
        const payload = {
          phone: String(this.createForm.phone || '').trim(),
          password: String(this.createForm.password || '').trim(),
          role: this.createForm.role,
          status: this.createForm.status,
          adStatus: this.createForm.adStatus
        }
        if (this.createForm.role === 1) {
          payload.commissionRate = this.createForm.commissionRate
          payload.deductRate = this.createForm.deductRate
        } else if (String(this.createForm.leaderId || '').trim()) {
          const leaderId = Number(this.createForm.leaderId)
          if (!Number.isInteger(leaderId) || leaderId <= 0) {
            this.$message.warning('所属创作者ID不正确')
            return
          }
          payload.leaderId = leaderId
        }
        this.createLoading = true
        try {
          await post('/admin/users', payload)
          this.$message.success('新增用户成功')
          this.createDialogVisible = false
          this.fetchUsers()
        } catch (e) {
          this.$message.error(e.message || '新增失败')
        } finally {
          this.createLoading = false
        }
      })
    },

    openEditDialog(user) {
      this.editUser = user
      this.editForm = {
        role: user.role != null ? user.role : 0,
        commissionRate: user.commissionRate != null ? user.commissionRate : 20,
        deductRate: user.deductRate != null ? user.deductRate : 10,
        status: user.status != null ? user.status : 0,
        adStatus: user.adStatus != null ? user.adStatus : 0
      }
      this.editDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.editFormRef) this.$refs.editFormRef.clearValidate()
      })
    },
    async handleEditSubmit() {
      if (!this.editUser) return
      const payload = {
        role: this.editForm.role,
        status: this.editForm.status
      }
      if (this.editForm.role === 1) {
        payload.adStatus = this.editForm.adStatus
        if (this.editForm.commissionRate != null) {
          payload.commissionRate = this.editForm.commissionRate
        }
        if (this.editForm.deductRate != null) {
          payload.deductRate = this.editForm.deductRate
        }
      }
      try {
        await put(`/admin/users/${this.editUser.id}`, payload)
        this.$message.success('保存成功')
        this.editDialogVisible = false
        this.fetchUsers()
      } catch (e) {
        this.$message.error(e.message || '保存失败')
      }
    },
    async openDetailDialog(user) {
      this.detailUser = user
      this.detailData = { currentCoins: null, totalEarned: null, deductAmount: null, todayRevenue: null }
      this.detailDialogVisible = true
      this.detailLoading = true
      try {
        const result = await get(`/admin/users/${user.id}/stats`)
        const data = (result && result.data) ? result.data : {}
        this.detailData = {
          currentCoins: data.currentCoins != null ? data.currentCoins : 0,
          totalEarned: data.totalEarned != null ? data.totalEarned : 0,
          deductAmount: data.deductAmount != null ? data.deductAmount : 0,
          todayRevenue: data.todayRevenue != null ? data.todayRevenue : 0
        }
      } catch (e) {
        this.$message.error(e.message || '获取用户详情失败')
      } finally {
        this.detailLoading = false
      }
    }
  }
}
</script>

<style scoped>
.text-muted {
  color: var(--color-text-muted, #909399);
}

.coin-text {
  font-weight: 600;
  color: #e6a23c;
}

.dialog-footer {
  text-align: center;
}

.detail-content {
  padding: 4px 0;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.detail-item {
  padding: 14px 12px;
  background: #f8fafc;
  border-radius: var(--radius-md, 8px);
  border: 1px solid var(--color-border, #e4e7ed);
  text-align: center;
}

.detail-label {
  font-size: 13px;
  color: var(--color-text-muted, #909399);
  margin-bottom: 8px;
}

.detail-value {
  font-size: 18px;
  font-weight: 700;
  color: var(--color-text, #303133);
}

.detail-value.highlight-green { color: #67c23a; }
.detail-value.highlight-gold { color: #e6a23c; }
.detail-value.highlight-red { color: #f56c6c; }
.detail-value.highlight-blue { color: var(--color-primary, #409eff); }

@media (max-width: 560px) {
  .detail-grid {
    grid-template-columns: 1fr;
  }
}
</style>